import axios from 'axios';
import React, { useState, useEffect, ReactNode } from 'react';

import styles from './downloadTable.scss';
import Detail, { Description, Tool } from './dataDetail';


type YearType = 'single' | 'multiple';
export type DataType = 'office' | 'exp' | 'conc' | 'admer' | 'statistic' | 'secular';
type DataRow = OfficeData | ExpData | ConcentrationData | AdmerData | StatisticData | SecularData;
type DataArray = DataRow[]

type YearTypes = {
  [key in YearType]: string;
}

const yearTypes: YearTypes = {
  'single': '単年データ',
  'multiple': '経年データ',
}

type DataTypes = {
  [key in DataType]: {
    'upper': string;
    'lower': string;
  };
}

const dataTypes: DataTypes = {
  'office': {
    'upper': '事業所データ',
    'lower': ''
  },
  'exp': {
    'upper': '排出量データ',
    'lower': ''
  },
  'conc': {
    'upper': '推計濃度データ',
    'lower': ''
  },
  'statistic': {
    'upper': '経年メッシュ統計データ',
    'lower': ''
  },
  'admer': {
    'upper': 'AIST-ADMER入力用',
    'lower': '排出量データ'
  },
  'secular': {
    'upper': '行政区域ごとの',
    'lower': '届出大気排出量経年データ'
  },
};

interface BaseConfig {
  year: number;
  dataType: DataType;
  yearType: YearType;
}

interface DataTable extends BaseConfig {
  data: DataArray;
}

interface OfficeData {
  name: string;
  zipPath: string;
}

interface ExpData {
  refNum: number;
  casRN: string;
  name: string;
  csvStdPath: string;
  csvStdSize: string;
  csvX5Path: string;
  csvX5Size: string;
  shpStdPath: string;
  shpStdSize: string;
  shpX5Path: string;
  shpX5Size: string;
}

interface ConcentrationData {
  refNum: number;
  casRN: string;
  name: string;
  csvStdPath: string;
  csvStdSize: string;
  csvX5Path: string;
  csvX5Size: string;
  shpStdPath: string;
  shpStdSize: string;
  shpX5Path: string;
  shpX5Size: string;
}

interface AdmerData {
  refNum: number;
  casRN: string;
  name: string;
  zipPath: string;
  zipSize: string;
}

interface StatisticData {
  refNum: number;
  casRN: string;
  name: string;
  zipPath: string;
  zipSize: string;
}

interface SecularData {
  refNum: number;
  casRN: string;
  name: string;
  csvPrefPath: string;
  csvPrefSize: string;
  csvMuniPath: string;
  csvMuniSize: string;
}

interface FormValues {
  refNum: string;
  cas: string;
  name: string;
}

type Errors = 'nodata' | 'notPublished';
type ErrorMsg = {
  [key in Errors]: string;
}

const initialStateErrorMsg = {
  nodata: '',
  notPublished: '',
}

const readFile = async (setTableData: React.Dispatch<React.SetStateAction<DataTable[]>>, setYear: React.Dispatch<React.SetStateAction<string>>) => {
  const url = `assets/config/prtrdata.json`;
  const { data } = await axios.get<DataTable[]>(url);
  setTableData(data);

  const years = new Set(data.filter(d => d.dataType != 'secular' && d.data.length > 0).map(d => d.year));
  const sortedYears = Array.from(years).sort((a, b) => b - a);
  setYear(String(sortedYears[0])); // latest year
}

const readDescription = async (dataType: DataType, year: string, setDescription: React.Dispatch<React.SetStateAction<string>>) => {
  const url = dataType == 'secular' || dataType == 'statistic' ? `assets/html/${dataType}_description.html` : `assets/html/${year}/${dataType}_description_${year}.html`;
  await fetch(url)
    .then(response => response.text())
    .then(data => setDescription(data))
    .catch(e => setDescription(""));
}

const createDownloadLink = (path: string, size: string) => {
  if (!path || path == '-') {
    return '-';
  } else if (path == '準備中') {
    return '準備中';
  }

  const segments = path.split('/');
  const filename = segments[segments.length - 1];
  const ext_segments = path.split('.');
  const ext = ext_segments[ext_segments.length - 1];

  return (<a href={path} download={filename}>{ext}[{size}]</a>);
}

const DownloadTable = () => {
  const [yearType, setYearType] = useState<YearType>('single');
  const [yearTypeButtons, setYearTypeButtons] = useState<ReactNode[]>([]);
  const [year, setYear] = useState('');
  const [dataType, setDataType] = useState<DataType>('exp');
  const [yearButtons, setYearButtons] = useState<ReactNode[]>([]);
  const [dataTypeButtons, setDataTypeButtons] = useState<ReactNode[]>([]);

  const [formValues, setFormValues] = useState<FormValues>({ refNum: '', cas: '', name: '' });
  const [tableData, setTableData] = useState<DataTable[]>([]);
  const [filteredData, setFilteredData] = useState<DataTable>();
  const [displayData, setDisplayData] = useState<ReactNode>(<></>);
  const [ascending, setAscending] = useState(true);
  const [errorMsg, setErrorMsg] = useState<ErrorMsg>(initialStateErrorMsg);

  const [isOpenDataTypeTable, setIsOpenDataTypeTable] = useState(false);
  const [isOpenDescription, setIsOpenDescription] = useState(false);
  const [isOpenTool, setIsOpenTool] = useState(false);

  const [description, setDescription] = useState("");

  console.log(filteredData)
  if (!displayData) {
    return;
  }

  useEffect(() => {
    if (!year) {
      return;
    }

    readDescription(dataType, year, setDescription);
  }, [dataType, year]);

  useEffect(() => {
    readFile(setTableData, setYear);
  }, []);

  useEffect(() => {
    if (yearType == 'single') {
      const years = new Set(tableData.filter(d => d.yearType == 'single' && d.data.length > 0).map(data => data.year));
      const sortedYears = Array.from(years).sort((a, b) => b - a);
      setYearButtons(sortedYears.map(sortedYear => <button className={isSelectedYear(String(sortedYear))} key={sortedYear} onClick={() => setYear(String(sortedYear))}>{sortedYear}年</button>));
      setYearTypeButtons(Array.from(new Set(tableData.map(d => d.yearType))).map(yType => <button className={isSelectedYearType(yType)} key={yType} onClick={() => setYearType(yType)}>{yearTypes[yType]}</button>));
      setDataTypeButtons(Object.entries(dataTypes).filter(([k, v]) => k != 'secular').map(([en, ja]) => <button className={isSelectedDataType(en)} key={en} onClick={() => setDataType(en)}>{ja.upper}{ja.lower}</button>));
      if (dataType == 'secular') {
        setDataType('exp');
      }
    } else {
      setYearButtons([undefined]);
      setYearTypeButtons(Array.from(new Set(tableData.map(d => d.yearType))).map(yType => <button className={isSelectedYearType(yType)} key={yType} onClick={() => setYearType(yType)}>{yearTypes[yType]}</button>));
      setDataType('secular');
      setDataTypeButtons(Object.entries(dataTypes).filter(([k, v]) => k == 'secular').map(([en, ja]) => <button className={styles.dataTypeSelectedMultiple} key={en} onClick={() => setDataType(en)}>{ja.upper}{ja.lower}</button>));
    }
  }, [tableData, year, yearType, dataType]);

  useEffect(() => {
    if (tableData.length == 0) {
      setErrorMsg(prev => { return { ...prev, notPublished: `${year}年の${dataTypes[dataType]['upper']}${dataTypes[dataType]['lower']}は公開されていません。` } });
      return;
    }

    if (yearType == 'multiple') {
      setFilteredData(tableData.filter(data => data.dataType == dataType && data.data.length > 0)[0]);
    } else {
      setFilteredData(tableData.filter(data => data.dataType == dataType && data.year == Number(year))[0]);
    }
  }, [tableData, year, yearType, dataType]);

  useEffect(() => {
    if (!filteredData) {
      setDisplayData(<></>);
      return;
    }
    if (!filteredData.data.length) {
      setDisplayData(<></>);
      return;
    }

    if (Object.values(formValues).every(v => !v)) {
      setErrorMsg(initialStateErrorMsg);
      setDisplayData(createTableBody(filteredData))
      return;
    }

    setDisplayData(createTableBody(filteredData));
    setErrorMsg(initialStateErrorMsg);
  }, [filteredData, year, dataType, formValues]);

  useEffect(() => {
    if (!displayData || !filteredData) {
      return;
    }
    if (filteredData.data.every(d => d.zipPathNew == '-')) {
      return;
    }

    if (!displayData.length) {
      setErrorMsg(prev => { return { ...prev, nodata: `管理番号: "${formValues.refNum}"\nCAS登録番号: "${formValues.cas}"\n名称: "${formValues.name}"に合致するデータはありません。` } });
    } else {
      setErrorMsg(initialStateErrorMsg);
    }
  }, [displayData]);

  useEffect(() => {
    if (!filteredData) {
      return;
    }

    if (filteredData.data.every(d => d.zipPathNew == '-')) {
      setErrorMsg(prev => { return { ...prev, notPublished: `${year}年の${dataTypes[dataType]['upper']}${dataTypes[dataType]['lower']}は公開されていません。` } });
      setDisplayData(<></>);
    }
  }, [filteredData]);

  useEffect(() => {
    if (ascending) {
      setFilteredData(prevState => {
        if (!!prevState) {
          return { ...prevState, data: prevState.data.sort((rowA, rowB) => rowA.refNum - rowB.refNum) }
        }
      });
    } else {
      setFilteredData(prevState => {
        if (!!prevState) {
          return { ...prevState, data: prevState.data.sort((rowA, rowB) => rowB.refNum - rowA.refNum) }
        }
      });
    }
  }, [ascending]);

  const condition = (row: DataRow) => {
    const refNumHalfWidth = formValues.refNum.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
    const casHalfWidth = formValues.cas.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
    if (!row.casRN.includes(casHalfWidth)) {
      return false;
    } else if (!row.name.includes(formValues.name)) {
      return false;
    } else if (!refNumHalfWidth) {
      return true;
    } else if (row.refNum != refNumHalfWidth) {
      return false;
    } else {
      return true;
    }
  }

  const createTableBody = (selectedData: DataTable) => {
    if (selectedData.dataType == 'office') {
      return selectedData.data.map((row, idx) => (
        <tr key={idx}>
          <td className={styles.tdData}><b>{year}年 個別事業所排出量データ</b>  {createDownloadLink(row.zipPathNew, row.zipSize)}</td>
        </tr>
      ));
    } else if (selectedData.dataType == 'admer' || selectedData.dataType == 'statistic') {
      return selectedData.data.filter(row => condition(row)).map((row, idx) => (
        <tr key={idx}>
          <td className={styles.tdData}>{row.refNum}</td>
          <td className={styles.tdData}>{row.casRN}</td>
          <td className={styles.tdData}>{row.name}</td>
          <td className={styles.tdData}>{createDownloadLink(row.zipPathNew, row.zipSize)}</td>
        </tr>
      ));
    } else if (selectedData.dataType == 'secular') {
      return selectedData.data.filter(row => condition(row)).map((row, idx) => (
        <tr key={idx}>
          <td className={styles.tdData}>{row.refNum}</td>
          <td className={styles.tdData}>{row.casRN}</td>
          <td className={styles.tdData}>{row.name}</td>
          <td className={styles.tdData}>{createDownloadLink(row.csvPrefPath, row.csvPrefSize)}</td>
          <td className={styles.tdData}>{createDownloadLink(row.csvMuniPath, row.csvMuniSize)}</td>
        </tr>
      ));
    } else {
      return selectedData.data.filter(row => condition(row)).map((row, idx) => (
        <tr key={idx}>
          <td className={styles.tdData}>{row.refNum}</td>
          <td className={styles.tdData}>{row.casRN}</td>
          <td className={styles.tdData}>{row.name}</td>
          <td className={styles.tdData}>{createDownloadLink(row.csvStdPathNew, row.csvStdSize)}</td>
          <td className={styles.tdData}>{createDownloadLink(row.csvX5PathNew, row.csvX5Size)}</td>
          <td className={styles.tdData}>{createDownloadLink(row.shpStdPathNew, row.shpStdSize)}</td>
          <td className={styles.tdData}>{createDownloadLink(row.shpX5PathNew, row.shpX5Size)}</td>
        </tr>
      ));
    }
  }

  const isSelectedYear = (selectedYear: string) => selectedYear === year ? styles.yearSelected : styles.yearNotSelected;
  const isSelectedYearType = (selectedYearType: YearType) => selectedYearType === yearType ? styles.yearTypeSelected : styles.yearTypeNotSelected;
  const isSelectedDataType = (selectedDataType: DataType) => selectedDataType === dataType ? styles.dataTypeSelected : styles.dataTypeNotSelected;

  const tHead = () => {
    if (!displayData.length) {
      return;
    }
    return (
      <thead>
        {(() => {
          if (dataType == 'office') {
            return (
              <></>
            );
          } else if (dataType == 'admer' || dataType == 'statistic') {
            return (
              <>
                <tr>
                  <th className={styles.refNum}>政令<br />番号</th>
                  <th className={styles.casRN}>CAS登録番号</th>
                  <th className={styles.name}>名称</th>
                  <th className={styles.admerData}>排出量<br />(３次メッシュ)</th>
                </tr>
              </>
            );
          } else if (dataType == 'secular') {
            return (
              <>
                <tr>
                  <th className={styles.refNum}>管理<br />番号</th>
                  <th className={styles.casRN}>CAS登録番号</th>
                  <th className={styles.name}>名称</th>
                  <th className={styles.data}>都道府県</th>
                  <th className={styles.data}>市区町村</th>
                </tr>
              </>
            );
          } else {
            return (
              <>
                <tr>
                  <th rowSpan={2} scope="col">政令<br />番号</th>
                  <th rowSpan={2} scope="col">CAS登録番号</th>
                  <th rowSpan={2} scope="col">名称</th>
                  <th colSpan={2} scope="col">csv</th>
                  <th colSpan={2} scope="col">シェープファイル</th>
                </tr>
                <tr>
                  {/* <th className={styles.refNum}>政令<br />番号</th>
                  <th className={styles.casRN}>CAS番号</th>
                  <th className={styles.name}>名称</th> */}
                  <th scope="col" className={styles.data}>3次<br />メッシュ</th>
                  <th scope="col" className={styles.data}>5倍<br />メッシュ</th>
                  <th scope="col" className={styles.data}>3次<br />メッシュ</th>
                  <th scope="col" className={styles.data}>5倍<br />メッシュ</th>
                </tr>
              </>
            );
          }
        })()}
      </thead>
    );
  }

  return (
    <>
      <p>　本ページでは、以下に示すPRTR制度に基づく排出量を地図上に表示するためのデータや排出量を利用した推計濃度データ等を提供しています。ダウンロードデータはGISソフトウエアにより、独自にデータの表示、解析、加工が可能です。<a href='./usage.html'>データの活用方法・利用例はこちら</a></p>
      <p>　表に記載のCAS登録番号は参考情報です。最新情報は<a href='https://www.chem-info.nite.go.jp/chem/chrip/chrip_search/intSrhSpcLst?_e_trans=&slScNm=RJ_02_002'>こちら</a>を御確認ください。</p>
      <div className={styles.accordion}>
        <Description dataType={dataType} dataName={dataTypes[dataType]['upper'] + dataTypes[dataType]['lower']} isOpenDescription={isOpenDescription} setIsOpenDescription={setIsOpenDescription} />
      </div>
      <div className={styles.accordion}>
        <Tool dataType={dataType} dataName={dataTypes[dataType]['upper'] + dataTypes[dataType]['lower']} isOpenTool={isOpenTool} setIsOpenTool={setIsOpenTool} />
      </div>


      <div className={styles.buttons}>
        <div className={styles.yearTypeButtons}>
          {yearTypeButtons}
        </div>
        <div className={dataType == 'secular' ? styles.dataTypeButtonsMultiple : styles.dataTypeButtons}>
          {dataTypeButtons}
        </div>
        <div className={yearType == 'single' ? styles.yearButtons : undefined}>
          {yearButtons}
        </div>
      </div>

      {(() => {
        return (
          <div className={styles.forms}>
            <p className={styles.description}>入力フォームを用いて、絞り込み検索ができます。</p>
            <input
              className={styles.refNumForm}
              value={formValues.refNum}
              placeholder='管理番号'
              onChange={e => setFormValues(prevState => { return { ...prevState, refNum: String(e.target.value) } })}
            />
            <input
              className={styles.form}
              value={formValues.cas}
              placeholder='CAS登録番号'
              onChange={e => setFormValues(prevState => { return { ...prevState, cas: String(e.target.value) } })}
            />
            <input
              className={styles.form}
              value={formValues.name}
              placeholder='名称'
              onChange={e => setFormValues(prevState => { return { ...prevState, name: e.target.value } })}
            />
            <button className={styles.clearButton} onClick={() => setFormValues({ refNum: '', cas: '', name: '' })}>クリア</button>
            {(() => {
              if (!!displayData.length && dataType != 'office') {
                return (
                  <>
                    {displayData.length}件
                  </>
                );
              }
            })()}
          </div>
        );
      })()}

      <div className={styles.accordion}>
        <Detail dataType={dataType} dataName={dataTypes[dataType]['upper'] + dataTypes[dataType]['lower']} isOpenDataTypeTable={isOpenDataTypeTable} setIsOpenDataTypeTable={setIsOpenDataTypeTable} dataDescription={description} />
      </div>

      {(() => {
        if (!!errorMsg.nodata || !!errorMsg.notPublished) {
          return (
            <div className={styles.errorMsg}>
              {errorMsg.nodata}
              {errorMsg.notPublished}
            </div>
          );
        }
      })()}
      <div>
        <table className={styles.dataTable2}>
          {tHead()}
          <tbody>
            {displayData}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default DownloadTable;